import React, { useCallback, useEffect, useState } from "react";
import Segmented from "@/components/Segmented";
import { Loader2, TriangleAlert, Truck, Undo2, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SurplusSweep from "./SurplusSweep";

const n = (v) => Number(v || 0).toLocaleString();
const SECTIONS = [["short", "Short across every job"], ["arrivals", "Arriving / overdue"],
  ["returns", "Coming back to the shop"], ["surplus", "Surplus sweep"]];

/** FLD-6 company-wide — what the shop and purchasing work top down. */
export default function CompanyMaterialStatus({ onChanged, sections = null, kpis = true }) {
  const SHOWN = sections ? SECTIONS.filter(([k]) => sections.includes(k)) : SECTIONS;
  const [data, setData] = useState(null);
  const [section, setSection] = useState(SHOWN[0][0]);

  const load = useCallback(async () => {
    try { setData((await api.get("/material/status/company")).data); }
    catch { toast.error("Could not read the company material status"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="p-6 text-center" data-testid="company-status-loading">
      <Loader2 className="w-4 h-4 animate-spin inline" />
    </div>;
  }
  const t = data.totals;

  return (
    <div className="space-y-3" data-testid="company-material-status">
      {kpis && (<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {[["red_rooms", "Red rooms", TriangleAlert, "text-red-700 bg-red-50 border-red-200"],
          ["not_on_hand", "Not on hand", TriangleAlert, "text-red-700 bg-red-50 border-red-200"],
          ["not_ordered", "Not ordered", TriangleAlert, "text-orange-800 bg-orange-50 border-orange-200"],
          ["overdue_arrivals", "Overdue arrivals", Truck, "text-amber-800 bg-amber-50 border-amber-200"],
          ["returns_pending", "Returns to receive", Undo2, "text-gray-700 bg-gray-50 border-gray-200"],
          ["surplus_units", "Surplus units", PackagePlus, "text-amber-900 bg-amber-50 border-amber-200"]]
          .map(([k, label, Icon, cls]) => (
          <div key={k} data-testid={`company-card-${k}`}
               className={`border rounded-lg p-2 ${t[k] > 0 ? cls : "bg-white border-gray-200"}`}>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500">
              <Icon className={`w-3 h-3 ${t[k] > 0 ? "" : "text-gray-400"}`} /> {label}
            </div>
            <div className={`text-2xl font-bold tabular-nums ${t[k] > 0 ? "" : "text-gray-300"}`}>
              {n(t[k])}
            </div>
          </div>
        ))}
      </div>)}

      <div data-testid="company-sections">
        <Segmented
          options={SHOWN.map(([k, label]) => ({ key: k, label, testId: `company-section-${k}` }))}
          value={section}
          onChange={setSection}
        />
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
        {section === "short" && (
          <table className="w-full text-[11px]" data-testid="company-short-table">
            <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left">Need by</th>
                <th className="px-2 py-1 text-left">Job</th>
                <th className="px-2 py-1 text-left">Room</th>
                <th className="px-2 py-1 text-left">Product</th>
                <th className="px-2 py-1 text-right">Not on hand</th>
                <th className="px-2 py-1 text-right">Not ordered</th>
                <th className="px-2 py-1 text-left">PO / ETA</th>
              </tr>
            </thead>
            <tbody>
              {data.short.slice(0, 300).map((s, i) => (
                <tr key={i} className={`border-t border-gray-100 ${s.flag === "red" ? "bg-red-50/70" : ""}`}
                    data-testid={`company-short-row-${i}`}>
                  <td className="px-2 py-1 whitespace-nowrap">
                    {s.need_by || "—"}
                    {s.days_out !== null && s.days_out <= 0 && (
                      <span className="ml-1 text-red-700 font-semibold">now</span>
                    )}
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">{s.job_number} {s.project_name}</td>
                  <td className="px-2 py-1">{s.room}</td>
                  <td className="px-2 py-1">{s.product}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-red-700 font-semibold">
                    {s.not_on_hand || ""}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-orange-800 font-semibold">
                    {s.not_ordered || ""}
                  </td>
                  <td className="px-2 py-1 text-gray-600">{s.po_ref}{s.eta ? ` · ${s.eta}` : ""}</td>
                </tr>
              ))}
              {data.short.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-6 text-center text-gray-500">
                  Nothing is short anywhere.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {section === "arrivals" && (
          <table className="w-full text-[11px]" data-testid="company-arrivals-table">
            <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left">ETA</th>
                <th className="px-2 py-1 text-left">Job</th>
                <th className="px-2 py-1 text-left">Product</th>
                <th className="px-2 py-1 text-right">Units</th>
                <th className="px-2 py-1 text-left">PO</th>
              </tr>
            </thead>
            <tbody>
              {data.arrivals.map((a, i) => (
                <tr key={i} className={`border-t border-gray-100 ${a.overdue ? "bg-red-50/70" : ""}`}
                    data-testid={`company-arrival-row-${i}`}>
                  <td className="px-2 py-1 whitespace-nowrap">
                    {a.eta} {a.overdue && <span className="text-red-700 font-semibold">overdue</span>}
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">{a.job_number} {a.project_name}</td>
                  <td className="px-2 py-1">{a.product}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{n(a.qty)}</td>
                  <td className="px-2 py-1 text-gray-600">{a.po_ref}</td>
                </tr>
              ))}
              {data.arrivals.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-6 text-center text-gray-500">
                  Nothing is due in this week.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {section === "returns" && (
          <table className="w-full text-[11px]" data-testid="company-returns-table">
            <thead className="text-[10px] uppercase text-gray-500 bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left">Sent</th>
                <th className="px-2 py-1 text-left">Job</th>
                <th className="px-2 py-1 text-left">Product</th>
                <th className="px-2 py-1 text-right">Units</th>
                <th className="px-2 py-1 text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.returns_pending.map((r) => (
                <tr key={r.id} className="border-t border-gray-100"
                    data-testid={`company-return-row-${r.id}`}>
                  <td className="px-2 py-1 whitespace-nowrap">{(r.sent_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.job_number} {r.project_name}</td>
                  <td className="px-2 py-1">{r.product}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{n(r.quantity)}</td>
                  <td className="px-2 py-1 text-gray-600">{r.reason}</td>
                </tr>
              ))}
              {data.returns_pending.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-6 text-center text-gray-500">
                  Nothing is in transit back to the shop.
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        {section === "surplus" && (
          <div className="p-2">
            <SurplusSweep onChanged={() => { load(); onChanged?.(); }} />
          </div>
        )}
      </div>
    </div>
  );
}
